/**
 * Platform Admin User Detection
 *
 * Centralized helper for detecting platform admin users.
 * Supports both canonical and legacy detection methods.
 *
 * Contract: UI_AUTHORIZATION_FEEDBACK_CONTRACT_V1
 * - INV-UI-3A: Role checks isolated to authz layer helpers
 * - This helper encapsulates legacy role detection to prevent inline checks
 */

interface UserData {
  isPlatformAdmin?: boolean;
  role?: string;
  [key: string]: unknown;
}

/**
 * Determines if a user is a platform admin
 *
 * Detection logic:
 * 1. Canonical: userData.isPlatformAdmin === true
 * 2. Legacy fallback: userData.role === 'admin' (for backward compatibility)
 *
 * @param userData - User data object from Firestore
 * @returns true if user is a platform admin (canonical or legacy)
 */
export function isPlatformAdminUser(userData: UserData | null | undefined): boolean {
  if (!userData) {
    return false;
  }

  // Canonical check
  if (userData.isPlatformAdmin === true) {
    return true;
  }

  // Legacy fallback for users with role='admin' field
  // This supports backward compatibility without exposing role checks to feature modules
  // eslint-disable-next-line no-restricted-syntax -- Centralized authz helper: role check isolated here
  if (userData.role === 'admin') {
    return true;
  }

  return false;
}
